// React Imports
import React, { useState } from "react";

// Internationalization Import
import i18n from "i18next";

// Internal Imports
import { fetchWrapper } from "../../helpers/fetchwrapper";

import DocumentViewer from "../../Controls/DocumentViewer";
import { helperMethods } from "../../helpers/helperMethods";
import { IUserDocument } from "../../Models/IUserDocument";
import DownloadNotification from "../../Controls/DownloadDialog";
import { baseUrl } from "../../helpers/constants";
import LuebPagedDataGrid from "../../Controls/LuebPagedDataGrid";
import {
  Button,
  createTableColumn,
  Link,
  MessageBar,
  TableCellLayout,
  TableColumnDefinition,
  Tooltip,
  Text,
} from "@fluentui/react-components";
import { IAcademyDocumentFolder2UserDocument } from "../../Models/IAcademyDocumentFolder2UserDocument";
import {
  Delete24Filled,
  DocumentPdfRegular,
  DocumentRegular,
  VideoClipRegular,
} from "@fluentui/react-icons";
import { IUserDocument2SpracheDto } from "../../Models/IUserDocument2Sprache";

// Props
interface IUserDocumentDocumentsProps {
  isAdmin: boolean;
  folderGuid?: string;
  documents?: IAcademyDocumentFolder2UserDocument[];
  refreshRequested: any;
}

// Function
function AcademyDocumentFolderDocuments(props: IUserDocumentDocumentsProps) {
  const [documentToView, setDocumentToView] = useState<any>(undefined);
  const [showDownloadNotification, setShowDownloadNotification] =
    useState(false);

  const downloadFile = (userDocument: IUserDocument) => {
    setShowDownloadNotification(true);
    helperMethods.downloadFile(userDocument, true).finally(() => {
      setShowDownloadNotification(false);
    });
  };

  const removeDocumentFromFolder = (userDocumentGuid: string) => {
    if (props.folderGuid && userDocumentGuid) {
      fetchWrapper
        .getWithAuth(
          `${baseUrl}/api/academy/RemoveDocumentFromFolder?userDocumentGuid=${userDocumentGuid}&academyDocumentFolderGuid=${props.folderGuid}`,
        )
        .then(() => {
          props.refreshRequested();
        });
    }
  };

  const columns: TableColumnDefinition<IAcademyDocumentFolder2UserDocument>[] =
    [
      createTableColumn<IAcademyDocumentFolder2UserDocument>({
        columnId: "documentType",

        renderHeaderCell: () => <DocumentRegular />,
        renderCell: (item) => {
          if (item.userDocument.docFileExtension.toLowerCase() == "mp4") {
            return (
              <TableCellLayout>
                <VideoClipRegular fontSize={24} />
              </TableCellLayout>
            );
          } else if (
            item.userDocument.docFileExtension.toLowerCase() == "pdf"
          ) {
            return (
              <TableCellLayout>
                <DocumentPdfRegular fontSize={24} />
              </TableCellLayout>
            );
          } else {
            return (
              <TableCellLayout>
                <DocumentRegular fontSize={24} />
              </TableCellLayout>
            );
          }
        },
      }),
      createTableColumn<IAcademyDocumentFolder2UserDocument>({
        columnId: "document",

        renderHeaderCell: () => i18n.t<string>("document"),
        renderCell: (item) => {
          return (
            <TableCellLayout>
              <Link
                onClick={() => {
                  if (
                    item &&
                    item.userDocument.docFileExtension &&
                    (item.userDocument.docFileExtension.toLowerCase() ==
                      "mp4" ||
                      item.userDocument.docFileExtension.toLowerCase() == "m4v")
                  ) {
                    setDocumentToView({
                      path:
                        baseUrl +
                        "/api/documents/GetDocumentFromPath?path=" +
                        item.userDocument.docPath,
                      tile: item.userDocument.displayDocFileName,
                    });
                  } else if (item) {
                    // Download
                    downloadFile(item.userDocument);
                  }
                }}
              >
                {item.userDocument.displayDocFileName}
              </Link>
            </TableCellLayout>
          );
        },
      }),

      createTableColumn<IAcademyDocumentFolder2UserDocument>({
        columnId: "delete",

        renderHeaderCell: () => "",
        renderCell: (item) => {
          if (props.isAdmin) {
            return (
              <TableCellLayout>
                <Tooltip
                  relationship="label"
                  content={i18n.t<string>("delete")}
                >
                  <Button
                    onClick={() => {
                      removeDocumentFromFolder(item.userDocumentGuid);
                    }}
                    appearance="transparent"
                    icon={<Delete24Filled />}
                  ></Button>
                </Tooltip>
              </TableCellLayout>
            );
          } else {
            return <div></div>;
          }
        },
      }),

      createTableColumn<IAcademyDocumentFolder2UserDocument>({
        columnId: "description",

        renderHeaderCell: () => i18n.t<string>("description"),
        renderCell: (item) => {
          return (
            <TableCellLayout>
              <Text>{item.userDocument.description}</Text>
            </TableCellLayout>
          );
        },
      }),

      createTableColumn<IAcademyDocumentFolder2UserDocument>({
        columnId: "language",

        renderHeaderCell: () => i18n.t<string>("sprache"),
        renderCell: (item) => {
          return (
            <TableCellLayout>
              {item.userDocument.dokumentSprachen?.map(
                (docSprache: IUserDocument2SpracheDto) => {
                  return (
                    <Text>
                      {i18n.language.includes("de")
                        ? docSprache.sprache?.nameDE
                        : docSprache.sprache?.nameEN}
                    </Text>
                  );
                },
              )}
            </TableCellLayout>
          );
        },
      }),
      // ------------Verfügbarkeitsdatum------------
      createTableColumn<IAcademyDocumentFolder2UserDocument>({
        columnId: "validUntil",
        renderHeaderCell: () => {
          return <Text> {i18n.t<string>("validUntil")}</Text>;
        },
        renderCell: (item) => {
          return (
            <div className="lueb-centerVertical">
              {item.userDocument.validUntil &&
              new Date(item.userDocument.validUntil).toLocaleDateString() !=
                "1.1.1"
                ? new Date(item.userDocument.validUntil).toLocaleDateString()
                : "-"}
            </div>
          );
        },
      }),
    ];

  return (
    <div style={{ marginTop: 10 }}>
      {showDownloadNotification && <DownloadNotification />}

      {documentToView && (
        <DocumentViewer
          onDismiss={() => setDocumentToView(undefined)}
          title={documentToView.title}
          path={documentToView.path}
          canDownload={true}
        />
      )}

      {props.documents && (
        <div>
          <LuebPagedDataGrid
            items={props.documents.sort((a: any, b: any) => {
              if (a.userDocument.description && b.userDocument.description) {
                return a.userDocument.description.localeCompare(
                  b.userDocument.description,
                );
              } else {
                return -1;
              }
            })}
            columns={columns}
            resizableColumns
            sortable
            columnSizingOptions={{
              documentType: {
                minWidth: 50,
                defaultWidth: 50,
              },
              document: {
                minWidth: 300,
                defaultWidth: 300,
              },
              delete: {
                minWidth: 50,
                defaultWidth: 50,
              },
            }}
          />
        </div>
      )}

      {props.documents && props.documents.length == 0 && (
        <MessageBar intent="info">
          {i18n.t<string>("noDocumentsFound")}
        </MessageBar>
      )}

      <div style={{ marginTop: 20 }}>
        <Text>{i18n.t<string>("documentsPersonalUse")}</Text>
      </div>
    </div>
  );
}

// Export Function
export default AcademyDocumentFolderDocuments;
